<!-- staff/StaffAttendance.vue -->
<template>
  <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
    <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
      Attendance History
    </h2>

    <!-- Stats Cards (Current Month) -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      <!-- Present -->
      <div class="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg p-4 flex items-center">
        <div class="p-3 bg-green-100 dark:bg-green-800 rounded-full mr-4">
          <svg class="w-6 h-6 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
        </div>
        <div>
          <p class="text-sm text-green-600 dark:text-green-400 font-medium">Present Days</p>
          <p class="text-2xl font-bold text-green-800 dark:text-green-200">{{ stats.present }}</p>
        </div>
      </div>

      <!-- Absent -->
      <div class="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg p-4 flex items-center">
        <div class="p-3 bg-red-100 dark:bg-red-800 rounded-full mr-4">
          <svg class="w-6 h-6 text-red-600 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </div>
        <div>
          <p class="text-sm text-red-600 dark:text-red-400 font-medium">Absent Days</p>
          <p class="text-2xl font-bold text-red-800 dark:text-red-200">{{ stats.absent }}</p>
        </div>
      </div>

      <!-- Late -->
      <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 rounded-lg p-4 flex items-center">
        <div class="p-3 bg-yellow-100 dark:bg-yellow-800 rounded-full mr-4">
          <svg class="w-6 h-6 text-yellow-600 dark:text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
        </div>
        <div>
          <p class="text-sm text-yellow-600 dark:text-yellow-400 font-medium">Late Arrivals</p>
          <p class="text-2xl font-bold text-yellow-800 dark:text-yellow-200">{{ stats.late }}</p>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex flex-wrap items-end gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-100 dark:border-gray-700">
      <div class="flex-1 min-w-[150px]">
        <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">From Date</label>
        <input 
          type="date" 
          v-model="filters.from_date" 
          class="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div class="flex-1 min-w-[150px]">
        <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">To Date</label>
        <input 
          type="date" 
          v-model="filters.to_date" 
          class="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      <div class="flex gap-2">
        <button @click="applyFilters" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">Apply</button>
        <button @click="clearFilters" class="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-white text-sm font-medium rounded-lg transition-colors">Clear</button>
      </div>
    </div>

    <!-- Table -->
    <div v-if="loading" class="text-sm text-gray-500">Loading...</div>
    <p v-else-if="!attendance.length" class="text-sm text-gray-500">No attendance records found for this period.</p>
    
    <div v-else class="overflow-x-auto -mx-6">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-gray-700/50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Check In</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Check Out</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Work Hours</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Overtime</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Late (mins)</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          <tr v-for="record in attendance" :key="record.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{{ formatDate(record.attendance_date) }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{{ formatTime(record.check_in) }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{{ formatTime(record.check_out) }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{{ record.work_hours || '-' }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{{ record.overtime_hours || '-' }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{{ record.late_minutes > 0 ? record.late_minutes : '-' }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm">
              <span :class="getStatusInfo(record.status_id, record.check_in).class" class="px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full">
                {{ getStatusInfo(record.status_id, record.check_in).label }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { ref, inject, onMounted } from 'vue'
import api from '@/services/api'

const applicantId = inject('applicantId')
const attendance = ref([])
const stats = ref({ present: 0, absent: 0, late: 0 })
const loading = ref(true)

const filters = ref({
  from_date: '',
  to_date: ''
})

const fetchAttendance = async () => {
  loading.value = true
  try {
    let url = `get_employee_attendance.php?id=${applicantId}`
    if (filters.value.from_date) url += `&from_date=${filters.value.from_date}`
    if (filters.value.to_date) url += `&to_date=${filters.value.to_date}`
    
    const { data } = await api.get(url)
    attendance.value = data?.attendance || []
    if (data?.stats) stats.value = data.stats
  } catch (error) {
    console.error('Error fetching attendance:', error)
  } finally {
    loading.value = false
  }
}

const applyFilters = () => fetchAttendance()
const clearFilters = () => {
  filters.value.from_date = ''
  filters.value.to_date = ''
  fetchAttendance()
}

// Formatters
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatTime = (dateTimeString) => {
  if (!dateTimeString) return '-'
  const date = new Date(dateTimeString)
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
}

// Maps status_id to a readable label and color
const getStatusInfo = (statusId, checkIn) => {
  if (statusId == 1) return { label: 'Present', class: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' }
  if (statusId == 2) return { label: 'Late', class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' }
  if (statusId == 3) return { label: 'Absent', class: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' }
  
  // Fallback based on clock-in data if status_id is missing
  if (checkIn) return { label: 'Present', class: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' }
  return { label: 'Absent', class: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' }
}

onMounted(() => {
  fetchAttendance()
})
</script>